import { HttpClient } from "@/shared/http";

import type {
  CreateUserGroupRequest,
  UpdateUserGroupRequest,
  UserGroupMembersRequest,
  UserGroupRecord,
} from "./UserGroupRecord";

/**
 * HTTP client for the user-groups REST API.
 *
 * A user group is a named, admin-managed collection of users used to gate
 * feature access (for example, by attaching the group to one or more feature
 * flags). All methods talk to the `v1/user-groups` endpoints and return the
 * server's authoritative `UserGroupRecord` so callers can refresh local state
 * from the response.
 *
 * Network and HTTP errors propagate as exceptions from the underlying
 * `HttpClient`; this class does not retry or swallow failures.
 */
export class UserGroupService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Fetch every user group visible to the caller.
   *
   * Pass `options.abortSignal` to cancel the in-flight request (for example,
   * when a React component unmounts). Aborting causes the underlying fetch to
   * reject, which surfaces here as a thrown error.
   */
  public async getAll(
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<UserGroupRecord[]> {
    const url = this.#httpClient.constructUrl("v1/user-groups");
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<UserGroupRecord[]>();
  }

  /**
   * Create a new user group from the supplied name and initial membership.
   *
   * Returns the persisted record (including the server-assigned id). Throws if
   * the server rejects the request — for example, when a group with the same
   * name already exists in the caller's org.
   */
  public async create(
    request: CreateUserGroupRequest,
  ): Promise<UserGroupRecord> {
    const url = this.#httpClient.constructUrl("v1/user-groups");
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
    });
    return response.json<UserGroupRecord>();
  }

  /**
   * Update the mutable fields (such as name or description) of an existing
   * user group identified by `userGroupId`.
   *
   * Membership is not changed by this call — use `addMembers` / `removeMembers`
   * for that. Returns the updated record.
   */
  public async update(
    userGroupId: string,
    request: UpdateUserGroupRequest,
  ): Promise<UserGroupRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/user-groups/id/${userGroupId}`,
    );
    const response = await this.#httpClient.put(url, {
      body: JSON.stringify(request),
    });
    return response.json<UserGroupRecord>();
  }

  /**
   * Permanently delete the user group identified by `userGroupId`.
   *
   * The server refuses the delete with a 409 Conflict if any active feature
   * flag still references the group; callers must detach the group from those
   * flags first. Resolves with no value on success; throws if the server
   * returns a non-2xx status (including the conflict case above, or when the
   * caller lacks permission, or the group does not exist).
   */
  public async remove(userGroupId: string): Promise<void> {
    const url = this.#httpClient.constructUrl(
      `v1/user-groups/id/${userGroupId}`,
    );
    const response = await this.#httpClient.delete(url);
    return response.throwIfError();
  }

  /**
   * Add the users in `request` to the group identified by `userGroupId`.
   *
   * The server treats the operation as idempotent: users already in the group
   * are left in place rather than causing an error. Returns the updated record
   * with the full post-add membership list.
   */
  public async addMembers(
    userGroupId: string,
    request: UserGroupMembersRequest,
  ): Promise<UserGroupRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/user-groups/id/${userGroupId}/members`,
    );
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
    });
    return response.json<UserGroupRecord>();
  }

  /**
   * Remove the users in `request` from the group identified by `userGroupId`.
   *
   * Users not currently in the group are silently ignored. Returns the updated
   * record with the full post-removal membership list.
   */
  public async removeMembers(
    userGroupId: string,
    request: UserGroupMembersRequest,
  ): Promise<UserGroupRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/user-groups/id/${userGroupId}/members`,
    );
    const response = await this.#httpClient.delete(url, {
      body: JSON.stringify(request),
    });
    return response.json<UserGroupRecord>();
  }
}
